import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

// Storage interface to match localStorage API
interface Storage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

// Values live only for the current page/session. Used when the browser won't
// give us persistent storage — better to lose a preference on reload than to
// throw on every read.
class MemoryStorage implements Storage {
  private data = new Map<string, string>();

  async getItem(key: string): Promise<string | null> {
    return this.data.get(key) ?? null;
  }

  async setItem(key: string, value: string): Promise<void> {
    this.data.set(key, value);
  }

  async removeItem(key: string): Promise<void> {
    this.data.delete(key);
  }
}

// Shared so a fallback keeps its values across getStorage() calls — segment.ts
// calls getStorage() per invocation and expects what it wrote to still be there.
const memory = new MemoryStorage();
let backend: Storage | undefined;

const degrade = (err: unknown): Storage => {
  if (backend !== memory) console.warn('[storage] persistent storage unavailable; using in-memory storage', err);
  backend = memory;
  return memory;
};

class AsyncStorage implements Storage {
  async getItem(key: string): Promise<string | null> {
    try {
      const { value } = await Preferences.get({ key });
      return value;
    } catch (err) {
      return degrade(err).getItem(key);
    }
  }

  async setItem(key: string, value: string): Promise<void> {
    try {
      await Preferences.set({ key, value });
    } catch (err) {
      await degrade(err).setItem(key, value);
    }
  }

  async removeItem(key: string): Promise<void> {
    try {
      await Preferences.remove({ key });
    } catch (err) {
      await degrade(err).removeItem(key);
    }
  }
}

// @capacitor/preferences' web implementation reads `window.localStorage`
// unguarded, so it throws when the browser hands back null or refuses access:
// Android WebViews with DOM storage disabled, iOS lockdown/private modes and
// embedded in-app browsers all do this. Probe once so we pick a single backend
// rather than splitting writes across two. Native platforms use the native
// plugin and never touch localStorage.
const isPersistentStorageUsable = (): boolean => {
  if (Capacitor.isNativePlatform()) return true;
  if (typeof window === 'undefined') return false;

  try {
    const store = window.localStorage;
    if (!store) return false;
    // Reads can succeed where writes throw (Safari private mode quota), so
    // exercise a write too.
    const probe = '__solve_storage_probe__';
    store.setItem(probe, probe);
    store.removeItem(probe);
    return true;
  } catch {
    return false;
  }
};

const getStorage = (): Storage => {
  if (!backend) backend = isPersistentStorageUsable() ? new AsyncStorage() : memory;
  return backend;
};

export default getStorage;
